import {IFilterControlOptions} from 'app/components/filters/CFilters'
import {
  MBusinessDetailGraphEntity,
  MPersonDetailGraphEntity,
} from 'app/modules/discovery/core/_models'
import {IfeFavorites, IfeRecentAtivity} from 'interfaces/IfeFavorites'
import {IInvitedMember} from 'interfaces/IInvitedMember'
import {IUserSubscriptionFE} from 'interfaces/IUserSubscriptionFE'
import {emGetInitials} from 'utilities/extensionMethods'

export interface AuthModel {
  api_token: string
  refreshToken?: string
}

export interface UserAddressModel {
  addressLine: string
  city: string
  state: string
  postCode: string
}

export interface UserCommunicationModel {
  email: boolean
  sms: boolean
  phone: boolean
}

export interface UserEmailSettingsModel {
  emailNotification?: boolean
  sendCopyToPersonalEmail?: boolean
  activityRelatesEmail?: {
    youHaveNewNotifications?: boolean
    youAreSentADirectMessage?: boolean
    someoneAddsYouAsAsAConnection?: boolean
    uponNewOrder?: boolean
    newMembershipApproval?: boolean
    memberRegistration?: boolean
  }
  updatesFromKeenthemes?: {
    newsAboutKeenthemesProductsAndFeatureUpdates?: boolean
    tipsOnGettingMoreOutOfKeen?: boolean
    thingsYouMissedSindeYouLastLoggedIntoKeen?: boolean
    newsAboutStartOnPartnerProductsAndOtherServices?: boolean
    tipsOnStartBusinessProducts?: boolean
  }
}

export interface UserSocialNetworksModel {
  linkedIn: string
  facebook: string
  twitter: string
  instagram: string
}

class Tenant {
  unique_id: string
  name: string

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.name = data.name
  }
}

class Subscriber {
  unique_id: string
  name: string
  tenant: Tenant

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.name = data.name
    this.tenant = new Tenant(data.tenant)
  }
}

class SubscriberAdmin {
  unique_id: string
  subscriber: Subscriber

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.subscriber = new Subscriber(data.subscriber)
  }
}

class SubscriberExplorer {
  unique_id: string
  subscriber: Subscriber

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.subscriber = new Subscriber(data.subscriber)
  }
}

class SubscriberAdminUserMembership {
  unique_id: string
  subscriber_admin: SubscriberAdmin

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.subscriber_admin = new SubscriberAdmin(data.subscriber_admin)
  }
}

class SubscriberExplorerUserMembership {
  unique_id: string
  subscriber_explorer: SubscriberExplorer

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.subscriber_explorer = new SubscriberExplorer(data.subscriber_explorer)
  }
}

export class CompanyAssociation {
  external_unique_id: string
  job_title: string
  external_item: any // Temporarily "any"

  constructor(data: any) {
    this.external_unique_id = data.external_unique_id
    this.job_title = data.job_title
    this.external_item = data.external_item
  }
}

export interface IEducation {
  id?: string
  name?: string
  city?: string
  state?: string
  completion?: string
  degree?: string
  fieldOfStudy?: string
  attending?: boolean
  beginDate?: string
  endDate?: string
  schoolUrl?: string
  description?: string
  activities?: string
}

export interface IExperience {
  id?: string
  name?: string
  city?: string
  state?: string
  beginDate?: string
  endDate?: string
  isCurrent?: boolean
  title?: string
  description?: string
  companyUrl?: string
}
export interface ICertification {
  id: string
  name: string
  city: string
  state: string
  beginDate: string
  endDate: string
}
export interface IOpportunity {
  id: string
  name: string
}

export interface IUserAddedPublication {
  id: string
  title?: string
  url?: string
}
export interface IUserAddedPatent {
  id: string
  title?: string
  url?: string
}

export interface ILocationAccessed {
  longitude: number
  latitude: number
  addressLong?: string
  poi?: string
  city?: string
  state?: string
  country?: string
  accessDate: Date
}

export interface IExcludedClaim {
  entity_type: string
  entity: MBusinessDetailGraphEntity | MPersonDetailGraphEntity
}

export interface ISubscriptionPrefernces {
  preferred_tenant_id?: string
  tenants?: Record<string, IUserSubscriptionFE[]>
  showCarousel?: boolean
}

export class UserProfile {
  first_name: string
  last_name: string
  //profile_picture_url?: string
  address_line1: string
  address_line2: string
  address_city: string
  address_state: string
  address_zip: string
  address_single_line: string
  work_phone: string
  mobile_phone: string
  linkedin_url: string
  twitter_url: string
  //bio: string
  //certifications: ICertification[]
  //experience?: IExperience[]
  //education?: IEducation[]
  //skills?: string[]
  is_public: boolean
  is_searchable: boolean
  full_name: string
  associated_businesses?: string[]
  profile_email: string
  favorites?: IfeFavorites
  filters?: {type: string; filters: IFilterControlOptions[]}[]
  recent_activity?: IfeRecentAtivity
  iscout_accessed_locations?: ILocationAccessed[]
  excluded_claims?: IExcludedClaim[]
  invited_members?: IInvitedMember[]
  subscription_preferences?: ISubscriptionPrefernces
  default_input?: string

  constructor(data: any) {
    this.first_name = data.first_name || ''
    this.last_name = data.last_name || ''
    //this.profile_picture_url = data.profile_picture_url
    this.address_line1 = data.address_line1 || ''
    this.address_line2 = data.address_line2 || ''
    this.address_city = data.address_city || ''
    this.address_state = data.address_state || ''
    this.address_zip = data.address_zip || ''
    this.address_single_line = data.address_single_line || ''
    this.work_phone = data.work_phone || ''
    this.mobile_phone = data.mobile_phone || ''
    this.linkedin_url = data.linkedin_url || ''
    this.twitter_url = data.twitter_url || ''
    //this.bio = data.bio || ''
    //this.certifications = data.certifications ?? []
    //this.experience = data.experience ?? []
    //this.education = data.education ?? []
    //this.skills = data.skills ?? []
    this.is_public = data.is_public || false
    this.is_searchable = data.is_searchable || false
    this.full_name = data.full_name || ''
    this.profile_email = data.email || ''
    this.associated_businesses = data.associated_businesses || []
    //this.opportunities = data.opportunities ?? []
    this.favorites = data.favorites ?? {}
    this.filters = data.filters ?? []
    this.recent_activity = data.recent_activity ?? {}
    this.iscout_accessed_locations = data.iscout_accessed_locations || []
    this.excluded_claims = data.excluded_claims ?? []
    this.invited_members = data.invited_members ?? []
    this.subscription_preferences = data.subscription_preferences
    this.default_input = data.default_input || ''
  }
}

// export class UserModel {
//   unique_id: string

//   email: string

//   date_created: string
//   date_last_active?: string

//   // avatar_url?: string // Deprecated ... this has moved into the profile

//   active_project_memberships: UserProjectMembership[]

//   last_used_project_type_unique_id?: string
//   last_used_project_unique_id?: string

//   available_tenants: Tenant[]

//   subscriber_admin_user_memberships: SubscriberAdminUserMembership[]
//   subscriber_explorer_user_memberships: SubscriberExplorerUserMembership[]

//   current_tenant_id?: string

//   profile: UserProfile

//   company_associations: CompanyAssociation[]
//   paid?: boolean

//   constructor(data: any) {
//     this.unique_id = data.unique_id
//     this.email = data.email
//     this.date_created = data.date_created
//     this.date_last_active = data.date_last_active
//     this.active_project_memberships = data.active_project_memberships.map((project: any) => {
//       return new UserProjectMembership(project)
//     })
//     this.last_used_project_type_unique_id = data.last_used_project_type_unique_id
//     this.last_used_project_unique_id = data.last_used_project_unique_id
//     this.available_tenants = data.available_tenants.map((tenant: any) => {
//       return new Tenant(tenant)
//     })
//     this.subscriber_admin_user_memberships = data.subscriber_admin_user_memberships.map(
//       (subscriberAdminUserMembership: any) => {
//         return new SubscriberAdminUserMembership(subscriberAdminUserMembership)
//       }
//     )
//     this.subscriber_explorer_user_memberships = data.subscriber_explorer_user_memberships.map(
//       (subscriberExplorerUserMembership: any) => {
//         return new SubscriberExplorerUserMembership(subscriberExplorerUserMembership)
//       }
//     )
//     this.profile = new UserProfile(data.profile)
//     this.company_associations = data.company_associations
//       ? data.company_associations.map((ca: any) => new CompanyAssociation(ca))
//       : []
//     this.paid = false
//   }

//   setPaid(paid: boolean) {
//     this.paid = paid
//   }

//   setCurrentTenantId(tenantId: string | undefined) {
//     this.current_tenant_id = tenantId
//   }

//   getActiveProjectMembershipsForSelectedTenant(): UserProjectMembership[] {
//     if (!this.current_tenant_id) {
//       return []
//     }
//     return this.active_project_memberships.filter(
//       (projectMembership) => projectMembership.project.tenant?.unique_id === this.current_tenant_id
//     )
//   }

//   getName(): string {
//     if (this.profile) {
//       return this.profile.full_name
//     }
//     return this.email
//   }
// }

export class SimpleUserModel {
  unique_id: string

  email: string

  first_name?: string
  last_name?: string
  name?: string

  date_created: string
  date_last_active?: string

  avatar_url?: string

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.email = data.email
    this.first_name = data.first_name
    this.last_name = data.last_name
    this.name = data.name
    this.date_created = data.date_created
    this.date_last_active = data.date_last_active
    this.avatar_url = data.avatar_url
  }

  getName(): string {
    if (this.name) {
      return this.name
    }
    if (this.first_name && this.last_name) {
      return `${this.first_name} ${this.last_name}`
    }
    if (this.first_name) {
      return this.first_name
    }
    if (this.last_name) {
      return this.last_name
    }
    return this.email
  }

  getInitials(): string {
    const name = this.getName()
    return emGetInitials(name)
  }
}

export class FileUpload {
  unique_id: string
  created_at: string
  updated_at: string
  file_url: string
  original_file_name: string
  file_type: string
  file_size: number

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.created_at = data.created_at
    this.updated_at = data.updated_at
    this.file_url = data.file_url
    this.original_file_name = data.original_file_name
    this.file_type = data.file_type
    this.file_size = data.file_size
  }
}
